import React, {useEffect, useRef} from 'react';
import Quill from 'quill';

export const HtmlEditor = ({value = '', onChange, placeholder = '', onImageUpload = null}) => {
    const containerRef = useRef(null);
    const hostRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onImageUploadRef = useRef(onImageUpload);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        onImageUploadRef.current = onImageUpload;
    }, [onImageUpload]);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) {
            return;
        }

        const modules = {
            toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'blockquote'],
                [{'list': 'ordered'}, {'list': 'bullet'}],
                ['link', 'image'],
                ['clean']
            ]
        };

        const quill = new Quill(host, {
            theme: 'snow',
            placeholder,
            modules,
        });

        // Security bound: Disallow inlining massive Base64 data: URIs in Firestore
        if (quill.clipboard && typeof quill.clipboard.addMatcher === 'function') {
            quill.clipboard.addMatcher('IMG', (node, delta) => {
                const src = node.getAttribute('src') || '';
                if (src.startsWith('data:')) {
                    return { ops: [] };
                }
                return delta;
            });
        }

        quillRef.current = quill;

        const toolbar = quill.getModule('toolbar');
        if (toolbar) {
            toolbar.addHandler('image', () => {
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.click();
                input.onchange = async () => {
                    const file = input.files?.[0];
                    if (file && onImageUploadRef.current) {
                        try {
                            const url = await onImageUploadRef.current(file);
                            if (url) {
                                const range = quill.getSelection(true) || { index: quill.getLength() };
                                quill.insertEmbed(range.index, 'image', url);
                                quill.setSelection(range.index + 1);
                            }
                        } catch (err) {
                            console.error("Inline image upload failed", err);
                        }
                    }
                };
            });
        }

        const handleTextChange = () => {
            if (onChangeRef.current) {
                onChangeRef.current(quill.root.innerHTML);
            }
        };

        quill.on('text-change', handleTextChange);

        return () => {
            quill.off('text-change', handleTextChange);
            try {
                const toolbar = quill.getModule('toolbar');
                if (toolbar && toolbar.container && toolbar.container.parentNode) {
                    toolbar.container.parentNode.removeChild(toolbar.container);
                }
            } catch {
                // Defensive cleanup
            }
            quillRef.current = null;
            if (host) {
                host.innerHTML = '';
            }
        };
    }, [placeholder]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) {
            return;
        }

        const current = quill.root.innerHTML;
        const next = value || '';

        if (current === next) {
            return;
        }

        if (!next) {
            quill.setText('', 'silent');
            return;
        }

        const selection = quill.getSelection();
        const delta = quill.clipboard.convert(next);
        quill.setContents(delta, 'silent');

        if (selection) {
            quill.setSelection(selection, 'silent');
        }
    }, [value]);

    return (
        <div ref={containerRef} className="quill-editor-wrapper">
            <div ref={hostRef} />
        </div>
    );
};



